import { useEffect, useState } from 'react';

export interface GuaziDownloadLinks {
  android: string;
  ios: string;
  harmony: string;
  tv: string;
}

export interface GuaziSiteConfig {
  siteName: string;
  domain: string;
  siteUrl: string;
  downloadPagePath: string;
  download: GuaziDownloadLinks;
  contactEmail: string;
  reportUrl: string;
}

/**
 * 外置链接默认值。
 * 线上真实值来自 public/guazi-config.json（运行时可替换，无需重新构建）。
 */
export const guaziDefaultConfig: GuaziSiteConfig = {
  siteName: '瓜子影视',
  domain: 'guaziyingshi.net',
  siteUrl: 'https://www.guaziyingshi.net',
  downloadPagePath: '/download',
  download: {
    android: 'https://www.guaziyingshi.net',
    ios: 'https://www.guaziyingshi.net',
    harmony: 'https://www.guaziyingshi.net',
    tv: 'https://www.guaziyingshi.net',
  },
  contactEmail: 'txby666666@163.com',
  reportUrl: 'https://formspree.io/f/mqpkqdlo',
};

let guaziCachedConfig: GuaziSiteConfig | null = null;

function guaziResolveConfigUrl(): string {
  const base = typeof __BASE_PATH__ === 'string' && __BASE_PATH__ ? __BASE_PATH__ : '/';
  const normalized = base.charAt(base.length - 1) === '/' ? base : `${base}/`;
  return `${normalized}guazi-config.json`;
}

function guaziMergeConfig(raw: Partial<GuaziSiteConfig>): GuaziSiteConfig {
  const download = raw.download || {};
  return {
    ...guaziDefaultConfig,
    ...raw,
    download: {
      android: download.android || guaziDefaultConfig.download.android,
      ios: download.ios || guaziDefaultConfig.download.ios,
      harmony: download.harmony || guaziDefaultConfig.download.harmony,
      tv: download.tv || guaziDefaultConfig.download.tv,
    },
  };
}

/**
 * 使用 XMLHttpRequest 读取外置 JSON，兼容老内核（不依赖 fetch / Promise API 支持）。
 */
export function guaziLoadSiteConfig(): Promise<GuaziSiteConfig> {
  if (guaziCachedConfig) {
    return Promise.resolve(guaziCachedConfig);
  }
  return new Promise<GuaziSiteConfig>((resolve) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', guaziResolveConfigUrl(), true);
      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) return;
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const parsed = JSON.parse(xhr.responseText) as Partial<GuaziSiteConfig>;
            guaziCachedConfig = guaziMergeConfig(parsed);
            resolve(guaziCachedConfig);
            return;
          } catch (err) {
            // 解析失败则回退默认配置
          }
        }
        guaziCachedConfig = guaziDefaultConfig;
        resolve(guaziCachedConfig);
      };
      xhr.onerror = () => {
        guaziCachedConfig = guaziDefaultConfig;
        resolve(guaziCachedConfig);
      };
      xhr.send();
    } catch (err) {
      guaziCachedConfig = guaziDefaultConfig;
      resolve(guaziDefaultConfig);
    }
  });
}

export function useGuaziSiteConfig() {
  const [config, setConfig] = useState<GuaziSiteConfig>(guaziCachedConfig || guaziDefaultConfig);
  const [loading, setLoading] = useState(!guaziCachedConfig);

  useEffect(() => {
    let mounted = true;
    guaziLoadSiteConfig().then((cfg) => {
      if (mounted) {
        setConfig(cfg);
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  return { config, loading };
}